import { FC, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  Typography,
} from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'

import { PhoneFieldController } from '../../../components/forms/fields/PhoneFieldController'
import { SelectController } from '../../../components/forms/fields/SelectController'
import { TextFieldController } from '../../../components/forms/fields/TextFieldController'
import { toast } from '../../../components/LsToast'
import { createInstructor, updateInstructor } from '../../../service/instructors.service'
import {
  CreateInstructorDto,
  DEFAULT_LANGUAGES,
  DEFAULT_LICENCES,
  InstructorDto,
  LANGUAGE_CODES,
  LanguageLevel,
  LICENCE_DISCIPLINES,
  LicenceLevel,
  UpdateInstructorDto,
} from '../../../service/types'
import { InstructorFormValues, useSchema } from '../schema'

type Props = {
  open: boolean
  onClose: () => void
  onSaved: () => void
  instructor?: InstructorDto | null
}

export const InstructorDialog: FC<Props> = ({ open, onClose, onSaved, instructor }) => {
  const { t } = useTranslation()
  const schema = useSchema()

  const { handleSubmit, control, reset } = useForm<InstructorFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      languages: DEFAULT_LANGUAGES,
      licences: DEFAULT_LICENCES,
      salaryPrivate: 0,
      salaryGroup: 0,
      isActive: true,
    },
  })

  useEffect(() => {
    if (instructor) {
      reset({
        name: instructor.name,
        phone: instructor.phone ?? '',
        email: instructor.email ?? '',
        languages: instructor.languages ?? DEFAULT_LANGUAGES,
        licences: instructor.licences ?? DEFAULT_LICENCES,
        salaryPrivate: instructor.salaryPrivate ?? 0,
        salaryGroup: instructor.salaryGroup ?? 0,
        isActive: instructor.isActive,
      })
    } else {
      reset({
        name: '',
        phone: '',
        email: '',
        languages: DEFAULT_LANGUAGES,
        licences: DEFAULT_LICENCES,
        salaryPrivate: 0,
        salaryGroup: 0,
        isActive: true,
      })
    }
  }, [instructor, open, reset])

  const onSubmit = async (data: InstructorFormValues) => {
    try {
      const payload = data as unknown as CreateInstructorDto & UpdateInstructorDto
      if (instructor) {
        await updateInstructor(instructor.id, payload)
      } else {
        await createInstructor(payload)
      }
      toast.success(t('general.notification.savedSuccessfully'))
      onSaved()
      onClose()
    } catch {
      toast.error(t('general.notification.unexpectedError'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (reason !== 'backdropClick') onClose()
      }}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {instructor ? t('instructors.editInstructor') : t('instructors.addInstructor')}
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="caption">{t('instructors.form.name')}</Typography>
              <TextFieldController name="name" control={control} />
            </Box>
            <Box>
              <Typography variant="caption">{t('instructors.form.phone')}</Typography>
              <PhoneFieldController name="phone" control={control} />
            </Box>
            <Box>
              <Typography variant="caption">{t('instructors.form.email')}</Typography>
              <TextFieldController name="email" control={control} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                {t('instructors.form.languages')}
              </Typography>
              <Box display="flex" flexDirection="column" gap={1} sx={{ mt: 0.5 }}>
                {LANGUAGE_CODES.map((code) => (
                  <Box key={code} display="flex" alignItems="center" gap={2}>
                    <Typography variant="body2" sx={{ width: 100, flexShrink: 0 }}>
                      {t(`languages.${code}`)}
                    </Typography>
                    <SelectController
                      name={`languages.${code}` as any}
                      control={control}
                      options={Object.values(LanguageLevel).map((level) => (
                        <MenuItem key={level} value={level}>
                          {t(`languageLevel.${level}`)}
                        </MenuItem>
                      ))}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                {t('instructors.form.licences')}
              </Typography>
              <Box display="flex" flexDirection="column" gap={1} sx={{ mt: 0.5 }}>
                {LICENCE_DISCIPLINES.map((discipline) => (
                  <Box key={discipline} display="flex" alignItems="center" gap={2}>
                    <Typography variant="body2" sx={{ width: 100, flexShrink: 0 }}>
                      {t(`licences.${discipline}`)}
                    </Typography>
                    <SelectController
                      name={`licences.${discipline}` as any}
                      control={control}
                      options={Object.values(LicenceLevel).map((level) => (
                        <MenuItem key={level} value={level}>
                          {t(`licenceLevel.${level}`)}
                        </MenuItem>
                      ))}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                {t('instructors.form.salaries')}
              </Typography>
              <Box display="flex" flexDirection="column" gap={1} sx={{ mt: 0.5 }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="body2" sx={{ width: 100, flexShrink: 0 }}>
                    {t('lessonType.private')}
                  </Typography>
                  <TextFieldController name="salaryPrivate" control={control} type="number" />
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="body2" sx={{ width: 100, flexShrink: 0 }}>
                    {t('lessonType.group')}
                  </Typography>
                  <TextFieldController name="salaryGroup" control={control} type="number" />
                </Box>
              </Box>
            </Box>
            {instructor && (
              <Box>
                <FormControlLabel
                  control={
                    <Controller
                      name="isActive"
                      control={control}
                      render={({ field }) => <Switch checked={field.value} onChange={field.onChange} />}
                    />
                  }
                  label={t('instructors.form.isActive')}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('general.button.cancel')}</Button>
          <Button type="submit" variant="contained">
            {t('general.button.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
